import { BroadcastsIcon } from "@minket/brand/icons/broadcasts";
import { SendIcon } from "@minket/brand/icons/send";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { broadcastStatus } from "@/components/broadcasts/format";
import { UnavailableAction } from "@/components/page/action";
import { GhostSections } from "@/components/page/ghost";
import { DetailPage } from "@/components/page/item";
import { Page } from "@/components/page/page";
import { LoadingState } from "@/components/page/states";
import { endpoints } from "@/lib/endpoints";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/broadcasts/$id")({
  component: BroadcastDetail,
});

// The editor is the one screen exempt from the route budget; it loads once the broadcast has.
const BroadcastEditor = lazy(() =>
  import("@/components/broadcasts/block-editor").then((m) => ({ default: m.BroadcastEditor })),
);

const sections = [
  { title: "Content", description: "", wide: true },

  { title: "Analytics", description: "", wide: true },
] as const;

function BroadcastDetail() {
  const { id } = Route.useParams();
  const { project } = useWorkspace();
  const broadcast = useQuery({
    ...apiQuery(endpoints.broadcast, { projectId: project?.id ?? "", broadcastId: id }),
    enabled: project !== null,
    // While it goes out, progress and analytics follow along.
    refetchInterval: (query) =>
      query.state.data && broadcastStatus(query.state.data.status).live ? 5000 : false,
  });

  if (project && broadcast.isSuccess) {
    return (
      <Suspense
        fallback={
          <Page
            title={broadcast.data.name}
            documentTitle={`${broadcast.data.name} · Broadcasts`}
            serial={{ id, label: "broadcast id" }}
            back={{ to: "/broadcasts", label: "Broadcasts" }}
            action={
              <UnavailableAction
                label="Send broadcast"
                icon={SendIcon}
                reason="Loading the editor"
              />
            }
          >
            <LoadingState label="Loading the editor">
              <GhostSections sections={sections} busy />
            </LoadingState>
          </Page>
        }
      >
        <BroadcastEditor
          key={broadcast.data.id}
          projectId={project.id}
          broadcast={broadcast.data}
        />
      </Suspense>
    );
  }

  return (
    <DetailPage
      kind="Broadcast"
      icon={BroadcastsIcon}
      id={id}
      back={{ to: "/broadcasts", label: "Broadcasts" }}
      action={{ label: "Send broadcast", icon: SendIcon }}
      noun="this broadcast"
      will="This page will hold the broadcast's editor, audience and schedule, its A/B test, live progress and analytics."
      sections={sections}
      query={(projectId) => apiQuery(endpoints.broadcast, { projectId, broadcastId: id })}
    />
  );
}
